import React, { useEffect, useState } from "react";

import { useGetDatasetMappersData } from "api/api-hooks/dataset-mapper-hooks";
import DatasetStatusShower from "components/basic/datasetStatusShower/DatasetStatusShower";
import useDefaultPlotlyConfig from "hooks/useDefaultPlotlyConfig";
import Plotly, { LayoutAxis } from "plotly.js";
import calculateYAxisRangeValues from "utils/math-utils";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import Plot from "react-plotly.js";
import { useResizeDetector } from "react-resize-detector";
import { cloneDeep } from "lodash-es";
import { T } from "services/lang/LanguageService";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { dweenColorway } from "utils/constants";
import { BaseDatasetDTO } from "api/model/dataset/BaseDatasetDTO";
import ExportCsvDialog from "./ExportCsvDialog";
import copyChartToClipBoard from "./chartUtils";
import DatasetLineChart, { IYaxisCLipValues } from "./dataset-line-chart/DatasetLineChart";

import {
  clipboardIcon,
  clipIcon,
  csvDownloadIcon,
  dialogIcon,
  forkIcon,
  logScaleIcon,
  statusIcon,
} from "./icons";
import { AxesConfig, DatasetChartProps, PlotDataEx } from "./interfaces";
import { splitLongWord, splitStringToLines } from "./utils";

const commonAxisConfig = {
  showspikes: true,
  spikemode: "toaxis",
  automargin: true,
  tickformat: "f",
} as Partial<LayoutAxis>;

const secondaryAxisConfig = {
  ...commonAxisConfig,
  overlaying: "y",
  showgrid: false,
};

const xAxisCommonConfig = {
  spikemode: "toaxis",
  showspikes: true,
};

const layoutConfig = {
  margin: {
    l: 0,
    r: 0,
    b: 30,
    t: 20,
  },
  hoverlabel: {
    align: "left",
  },
} as Plotly.Layout;

interface BasicChartProps {
  selectedDatasets: Array<BaseDatasetDTO>;
  data: DatasetDataInfoResponse[];
  plotData: PlotDataEx[];
  chartProps?: DatasetChartProps;
}

export default function BasicChart({
  selectedDatasets,
  data,
  plotData,
  chartProps,
}: BasicChartProps) {
  const { width, height, ref } = useResizeDetector();
  const [traces, setTraces] = useState<Array<Partial<PlotDataEx>>>([]);
  const [axesConfig, setAxesConfig] = useState<AxesConfig>({});
  const [axesGrouped, setAxesGrouped] = useState<boolean>(chartProps?.toggleTracesGrouping ?? true);
  const [isScatterChart, setIsScatterChart] = useState<boolean>(false);
  const [logScale, setLogScale] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [renderCount, setRenderCount] = useState<number>(0);
  const identifiers = plotData.map(dataset => dataset.identifier);
  const [initialTraces, setInitialTraces] = useState<Array<Partial<PlotDataEx>>>(plotData);
  const [chartDialog, setChartDialog] = useState<boolean>(false);
  const [clip, setClip] = useState(false);

  const [axisTickMappings, setAxisTickMappings] = useState<
    Record<string, { tickvals: number[]; ticktext: string[] }>
  >({});

  const [statusDialog, setStatusDialog] = useState<boolean>(false);
  const [exportCsvDialog, setExportCsvDialog] = useState<boolean>(false);

  const filteredIdentifiers = identifiers.filter(id => id !== undefined) as (string | null)[];
  const { data: mapperDataResponse, isLoading: isMapperDataLoading } = useGetDatasetMappersData();

  const defaultConfig = useDefaultPlotlyConfig();

  useEffect(() => {
    if (chartProps?.selectedChartType === "SCATTER") {
      setIsScatterChart(true);
    } else {
      setIsScatterChart(false);
    }
  }, [chartProps?.selectedChartType]);

  useEffect(() => {
    loadData();
  }, [plotData]);

  useEffect(() => {
    setRenderCount(renderCount + 1);
  }, [width, traces, loaded, axesConfig, axesGrouped]);

  useEffect(() => {
    if (initialTraces.length > 0) {
      const traceNames = new Set();
      for (let i = 0; i < initialTraces.length; i++) {
        if (initialTraces[i] !== undefined) {
          const dataset = selectedDatasets?.find(
            ds => ds.identifier === initialTraces[i].identifier
          );

          if (dataset) {
            const { currentLineConfig } = dataset;

            if (currentLineConfig) {
              initialTraces[i].currentLineConfig = currentLineConfig;
            }

            if (
              initialTraces[i] &&
              (currentLineConfig?.color || currentLineConfig?.width || currentLineConfig?.dashType)
            ) {
              initialTraces[i].line = {
                color: currentLineConfig?.color,
                width: currentLineConfig?.width,
                dash: currentLineConfig?.dashType,
              };
            }
          }

          if (
            typeof initialTraces[i].name === "string" &&
            !initialTraces[i].name?.includes(`(${initialTraces[i].unit})`)
          ) {
            const newTraceName = `${initialTraces[i].name} (${initialTraces[i].unit})`;
            initialTraces[i].name = newTraceName;
          }

          const traceName = initialTraces[i].name;
          if (traceNames.has(traceName)) {
            initialTraces[i].showlegend = false;
          } else {
            initialTraces[i].showlegend = true;
            traceNames.add(traceName);
          }

          initialTraces[i].legendgroup = traceName;
        }
      }

      if (axesGrouped) {
        groupByUnitTraces(initialTraces);
      } else {
        ungroupTraces(initialTraces);
      }

      setTraces(initialTraces);
    }
    setLoaded(true);
  }, [initialTraces, axesGrouped, width]);

  useEffect(() => {
    if (!isMapperDataLoading) {
      loadData();
    }
  }, [selectedDatasets]);

  const onRefresh = () => {
    if (!isMapperDataLoading) {
      loadData();
    }
  };

  const clipAxis = () => {
    const newClip = !clip;

    let clipValues: IYaxisCLipValues[] = [];
    if (newClip) {
      clipValues = calculateYAxisRangeValues(data);
    }

    setClip(newClip);
    const updatedTraces = [...traces];

    if (axesGrouped) {
      groupByUnitTraces(updatedTraces, clipValues);
    } else {
      ungroupTraces(updatedTraces, clipValues);
    }

    setTraces(updatedTraces);
  };

  const toggleGroupingHandler = () => {
    setAxesGrouped(currentAxesGrouped => !currentAxesGrouped);
  };
  const loadData = () => {
    setLoading(true);

    const newPlotData = cloneDeep(plotData);
    if (
      chartProps?.selectedChartType === "LINE" &&
      Array.isArray(mapperDataResponse) &&
      Array.isArray(newPlotData) &&
      newPlotData.length >= 1
    ) {
      for (let i = 0; i < newPlotData.length; i += 1) {
        const currentDataset = selectedDatasets?.find(
          dataset => dataset.identifier === newPlotData[i].identifier
        );
        if (currentDataset) {
          const currentMapper = mapperDataResponse.find(
            mapper => Number(mapper.id) === currentDataset?.stringValueMapper
          );
          if (currentMapper) {
            const uniqueYValues = new Set(
              (newPlotData[i].y as number[]).filter(y => typeof y === "number")
            );

            const usedTickEntries = Object.entries(currentMapper.mappings).filter(([key]) =>
              uniqueYValues.has(Number(key))
            );

            const tickvals = usedTickEntries.map(([key]) => Number(key));
            const ticktext = usedTickEntries.map(([, value]) => {
              const stringVal = String(value);
              const lines = splitStringToLines(stringVal);
              return lines
                .map((line, idx) => (idx === 0 || line.length === 15 ? line : splitLongWord(line)))
                .join("<br>");
            });

            const valueToIndexMap = new Map<number, number>();
            tickvals.forEach((val, index) => {
              valueToIndexMap.set(val, index);
            });

            setAxisTickMappings(prev => ({
              ...prev,
              [currentMapper.title]: {
                tickvals: tickvals.map((_, index) => index),
                ticktext,
              },
            }));

            const numericY = newPlotData[i].y;
            const newNumericY = (numericY as number[]).map(y => valueToIndexMap.get(y) ?? 0);

            newPlotData[i].y = newNumericY;

            newPlotData[i].hoverinfo = "text";
            newPlotData[i].hovertemplate =
              `<b>%{customdata}</b> at %{x}<br>${newPlotData[i].name}<extra></extra>`;
            newPlotData[i].customdata = Array.from(numericY as number[]).map(
              (value): string | number => {
                if (typeof value !== "number") return String(value ?? "");

                const mapped = currentMapper.mappings?.[value];
                const mappedLabel =
                  typeof mapped === "string"
                    ? `${splitStringToLines(mapped)
                        .map((line, idx) =>
                          idx === 0 || line.length === 15 ? line : splitLongWord(line)
                        )
                        .join("<br>")} ${value}`
                    : `${mapped} ${value}`;

                return mappedLabel;
              }
            );

            newPlotData[i].unit = currentMapper.title;
          }
        }
      }
    }

    setTimeout(() => {
      setInitialTraces(newPlotData);
    }, 600);

    setLoading(false);
  };

  const groupByUnitTraces = (
    currentData: Array<Partial<PlotDataEx>>,
    clipValues: IYaxisCLipValues[] = []
  ) => {
    const unitMap = new Map<string, string>();
    const axes: AxesConfig = {};
    const domain = [0, 1];

    for (let i = 0; i < currentData.length; i++) {
      const unit = currentData[i].unit!!;
      const trace = currentData[i];
      const { identifier } = trace;

      const clipEntry = clipValues.find(c => c.identifier === identifier);

      const yAxisMaxValue = clipEntry?.yAxisMax;
      const yAxisMinValue = clipEntry?.yAxisMin;
      if (!unitMap.has(unit)) {
        const axCount = unitMap.size + 1;
        if (axCount === 1) {
          unitMap.set(unit, "y");
          axes.yaxis = createAxisConfig(
            axCount,
            unit!,
            domain,
            currentData[i].currentLineConfig?.range,
            yAxisMaxValue,
            yAxisMinValue
          );
        } else {
          unitMap.set(unit, `y${axCount}`);

          axes[`yaxis${axCount}`] = createAxisConfig(
            axCount,
            unit!,
            domain,
            currentData[i].currentLineConfig?.range,
            yAxisMaxValue,
            yAxisMinValue
          );
        }
      }
      trace.yaxis = unitMap.get(unit)!!;
    }

    axes.xaxis = { ...xAxisCommonConfig, domain } as LayoutAxis;

    setAxesConfig(axes);
  };

  const ungroupTraces = (
    currentData: Array<Partial<PlotDataEx>>,
    clipValues: IYaxisCLipValues[] = []
  ) => {
    const axes: AxesConfig = {};
    const domain = [0, 1];
    for (let i = 0; i < currentData.length; i += 1) {
      const { unit } = currentData[i];
      const trace = currentData[i];
      const { identifier } = trace;

      const axis = i + 1;
      const clipEntry = clipValues.find(c => c.identifier === identifier);

      const yAxisMaxValue = clipEntry?.yAxisMax;
      const yAxisMinValue = clipEntry?.yAxisMin;

      if (axis === 1) {
        if (currentData[i].currentLineConfig?.range !== undefined) {
          axes.yaxis = createAxisConfig(
            axis,
            unit!,
            domain,
            currentData[i].currentLineConfig?.range,
            yAxisMaxValue,
            yAxisMinValue
          );
        } else {
          axes.yaxis = createAxisConfig(axis, unit!, domain);
        }
      } else {
        if (currentData[i].currentLineConfig?.range !== undefined) {
          axes[`yaxis${axis}`] = createAxisConfig(
            axis,
            unit!,
            domain,
            currentData[i].currentLineConfig?.range,
            yAxisMaxValue,
            yAxisMinValue
          );
        } else {
          axes[`yaxis${axis}`] = createAxisConfig(axis, unit!, domain);
        }
        trace.yaxis = `y${axis}`;
      }
    }
    axes.xaxis = { ...xAxisCommonConfig, domain } as LayoutAxis;
    setAxesConfig(axes);
  };

  const createAxisConfig = (
    axis: number,
    unit: string,
    domain: Array<number>,
    axisRange?: { min: number | undefined; max: number | undefined },
    yAxisMaxValue?: number | undefined,
    yAxisMinValue?: number | undefined
  ): LayoutAxis => {
    const localDomain = [...domain];
    if (axis === 1) {
      return {
        range: [
          chartProps?.currentYAxisRange?.min ?? axisRange?.min,
          chartProps?.currentYAxisRange?.max ?? axisRange?.max,
        ],
        autorange: !(
          (chartProps?.currentYAxisRange?.max !== undefined || axisRange?.max !== undefined) &&
          (chartProps?.currentYAxisRange?.min !== undefined || axisRange?.min !== undefined)
        ),
        autorangeoptions: {
          clipmax: yAxisMaxValue,
          clipmin: yAxisMinValue,
        },
        title: {
          text: unit,
          standoff: 10,
        },
        ...commonAxisConfig,
      } as LayoutAxis;
    }
    if (axis === 2) {
      return {
        range: [axisRange?.min, axisRange?.max],
        autorange: !(axisRange?.max !== undefined && axisRange?.min !== undefined),
        autorangeoptions: {
          clipmax: yAxisMaxValue,
          clipmin: yAxisMinValue,
        },
        title: {
          text: unit,
          standoff: 10,
        },
        side: "right",
        anchor: "x",
        ...secondaryAxisConfig,
      } as LayoutAxis;
    }
    let axOffset;
    const pxVal = 100 / width!!;
    const offVal = pxVal * 0.75;

    // Left
    if (axis % 2 === 0) {
      axOffset = Math.floor(axis / 2) * offVal - offVal * 2;
      localDomain[0] = Math.max(0, axOffset) + offVal;
    }
    // Right
    else {
      axOffset = 1 - Math.floor(axis / 2) * offVal + offVal;

      if (axOffset > 0) {
        localDomain[1] = axOffset - offVal - offVal / 3;
      }
    }
    return {
      title: {
        text: unit,
        standoff: 4,
      },
      range: [axisRange?.min, axisRange?.max],
      autorange: !(axisRange?.max !== undefined && axisRange?.min !== undefined),
      position: axOffset,
      side: "left",
      anchor: "free",
      ...secondaryAxisConfig,
    } as LayoutAxis;
  };

  const getChartHeight = (): number | undefined => {
    if (chartProps?.height != null) {
      return chartProps?.height as number;
    }
    if (chartProps?.height ? chartProps?.height <= 0 : false) {
      return height;
    }
    return 400;
  };

  const chartConfig: Partial<Plotly.Config> = {
    ...defaultConfig,
    modeBarButtonsToAdd: [
      ...(chartProps?.selectedChartType === "LINE"
        ? [
            {
              title: T("chart.config.toggle-y-axis-clip"),
              name: "Toggle Y Axis Clip",
              icon: clipIcon,
              click: clipAxis,
            },
            {
              title: T("chart-config.show-statistics"),
              name: "Show Statistics",
              icon: statusIcon,
              click: () => setStatusDialog(prevState => !prevState),
            },
            {
              title: `${T("action.download")} CSV`,
              name: "Download CSV",
              icon: csvDownloadIcon,
              click: () => setExportCsvDialog(true),
            },
            {
              title: T("chart-config.chart-in-dialog"),
              name: "Chart in Dialog",
              icon: dialogIcon,
              click: () => setChartDialog(prevState => !prevState),
            },
          ]
        : []),
      {
        title: T("chart-config.toggle-trace-grouping"),
        name: "Toggle trace grouping",
        icon: forkIcon,
        click: toggleGroupingHandler,
      },
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
      ...(isScatterChart
        ? [
            {
              title: T("chart-config.toggle-y-axis-log-scale"),
              name: "Log Scale",
              icon: logScaleIcon,
              click: () => {
                setLogScale(currentLogScale => !currentLogScale);
              },
            },
          ]
        : []),
    ],
  };
  const dataToExportInCSV =
    data?.map(dataset => ({
      identifier: dataset.identifier,
      name: dataset.title,
      type: "scatter",
      mode: "lines",
      unit: dataset.unit,
      showlegend: true,
      x: dataset.data.map(d => d.key),
      y: dataset.data.map(d => d.value),
    })) || [];

  return (
    <div ref={ref} className="p-m-2 overflow-hidden" style={{ minHeight: "300px" }}>
      {statusDialog && (
        <DatasetStatusShower dialog={statusDialog} setDialog={setStatusDialog} datasets={data} />
      )}
      {chartDialog && (
        <Dialog
          visible={chartDialog}
          onHide={() => setChartDialog(false)}
          style={{ width: "50vw" }}
          draggable
          resizable
          modal={false}
        >
          <DatasetLineChart
            title={T("word.chart")}
            dateFrom={chartProps?.dateFrom}
            dateTo={chartProps?.dateTo}
            identifiers={filteredIdentifiers.filter(identifier => identifier !== null)}
            showDateSelection
            chartDialogOpen={chartDialog}
          />
        </Dialog>
      )}
      {exportCsvDialog && (
        <ExportCsvDialog
          dialog={exportCsvDialog}
          setDialog={setExportCsvDialog}
          data={dataToExportInCSV}
          chartType="LINE"
        />
      )}
      {loaded && !chartProps?.hideRefresh && (
        <div className="flex align-items-center p-mx-2">
          <Button
            icon="pi pi-refresh"
            type="button"
            loading={loading}
            onClick={onRefresh}
            className="p-mr-2"
          />
        </div>
      )}
      {!loaded || chartProps?.loading || isMapperDataLoading || loading ? (
        <div className="flex justify-content-center p-mb-4">
          <ProgressSpinner
            style={{ width: "20rem", height: "20rem" }}
            strokeWidth="4"
            animationDuration="1s"
          />
        </div>
      ) : (
        <Plot
          className="ea-plotly-chart"
          config={chartConfig}
          data={[...traces]}
          layout={{
            ...axesConfig,
            ...layoutConfig,
            margin: { t: 20, b: 0, l: 0, r: 0, pad: 10 },
            legend: chartProps?.legend || { orientation: "h" },
            xaxis: chartProps?.xaxisName ? { title: { text: chartProps.xaxisName } } : {},
            ...Object.entries(axesConfig).reduce(
              (acc: Record<string, LayoutAxis>, [axisKey, axisConfig]) => {
                acc[axisKey] = {
                  ...axisConfig,
                  ...(axisConfig.title &&
                  typeof axisConfig.title === "object" &&
                  axisConfig.title.text &&
                  axisTickMappings[axisConfig.title.text]
                    ? axisTickMappings[axisConfig.title.text]
                    : {}),
                  ...(axisKey === "yaxis" && logScale && isScatterChart ? { type: "log" } : {}),
                };
                return acc;
              },
              {}
            ),
            width: width!!,
            height: getChartHeight(),
            colorway: dweenColorway,
            hovermode: "closest",
          }}
        />
      )}
    </div>
  );
}
